/**
 * Budget API client — functions for all budget endpoints.
 *
 * Requirements: 45.1, 45.4, 45.6, 45.8, 45.11, 45.12, 45.14, 45.16
 */

import { apiClient } from '@/services/apiClient';
import type { Budget, BudgetLineItem } from '@/types';

export interface BudgetDetailResponse {
  budget: Budget;
  lineItems: BudgetLineItem[];
}

export interface CreateBudgetInput {
  fiscalYear: number;
  budgetName?: string | null;
  approvalStatus?: string | null;
  notes?: string | null;
}

export interface UpdateBudgetInput {
  budgetName?: string | null;
  approvalStatus?: string | null;
  notes?: string | null;
  updatedAt: string;
}

export interface CreateLineItemInput {
  glCode?: string | null;
  description?: string | null;
  budgetCategory?: string | null;
  vendorId?: number | null;
  siteNumber?: string | null;
  month01?: number; month02?: number; month03?: number;
  month04?: number; month05?: number; month06?: number;
  month07?: number; month08?: number; month09?: number;
  month10?: number; month11?: number; month12?: number;
  notes?: string | null;
}

export interface BudgetVsActualLineItem {
  lineItemId: number;
  glCode: string | null;
  description: string | null;
  budgetCategory: string | null;
  budgetedAmount: number;
  actualAmount: number;
  dollarVariance: number;
  percentVariance: number | null;
  status: 'green' | 'yellow' | 'red';
}

export interface BudgetVsActualResponse {
  budgetId: number;
  fiscalYear: number;
  lineItems: BudgetVsActualLineItem[];
  totalBudgeted: number;
  totalActual: number;
  totalDollarVariance: number;
  totalPercentVariance: number | null;
}

export interface BudgetVsActualParams {
  glCode?: string;
  budgetCategory?: string;
  vendorId?: number;
  siteNumber?: string;
  period?: 'monthly' | 'quarterly' | 'annual';
}

export interface CopyBudgetInput {
  targetYear: number;
  adjustmentPercent?: number;
}

export const budgetService = {
  /** List all budgets. */
  list(): Promise<Budget[]> {
    return apiClient.get('budgets');
  },

  /** Get budget detail with line items. */
  getById(id: number): Promise<BudgetDetailResponse> {
    return apiClient.get(`budgets/${id}`);
  },

  /** Create a new budget. */
  create(input: CreateBudgetInput): Promise<Budget> {
    return apiClient.post('budgets', input);
  },

  /** Update a budget. */
  update(id: number, input: UpdateBudgetInput): Promise<Budget> {
    return apiClient.put(`budgets/${id}`, input);
  },

  /** Delete a budget. */
  delete(id: number): Promise<void> {
    return apiClient.delete(`budgets/${id}`);
  },

  /** Add a line item to a budget. */
  createLineItem(budgetId: number, input: CreateLineItemInput): Promise<BudgetLineItem> {
    return apiClient.post(`budgets/${budgetId}/line-items`, input);
  },

  /** Update a line item. */
  updateLineItem(lineItemId: number, input: CreateLineItemInput): Promise<BudgetLineItem> {
    return apiClient.put(`budgets/line-items/${lineItemId}`, input);
  },

  /** Delete a line item. */
  deleteLineItem(lineItemId: number): Promise<void> {
    return apiClient.delete(`budgets/line-items/${lineItemId}`);
  },

  /** Get budget vs actual comparison. */
  getVsActual(budgetId: number, params?: BudgetVsActualParams): Promise<BudgetVsActualResponse> {
    return apiClient.get(
      `budgets/${budgetId}/vs-actual`,
      params as Record<string, string | number | boolean | undefined>,
    );
  },

  /** Copy budget from prior year. */
  copy(budgetId: number, input: CopyBudgetInput): Promise<Budget> {
    return apiClient.post(`budgets/${budgetId}/copy`, input);
  },

  /** Export budget to CSV (returns download URL). */
  getExportUrl(budgetId: number): string {
    return `/api/budgets/${budgetId}/export`;
  },
};
